// itemTooltip: pure functions building the game-style item tooltip. Ported
// from mogugi; funnyresources only publishes item names, so enchants,
// metalware and upgrades are shown by id instead of by name.
import { parseItemMetadata, type Holder } from './itemIndex';
import { itemName } from './itemNames';

// Effect-line param codes (verified against OptionList SetParamOnEquip).
const PARAM_NAMES: Record<number, string> = {
    1: 'Max HP', 3: 'Max MP', 16: 'Max Damage', 19: 'Critical', 20: 'Protection',
    22: 'Balance', 53: 'Magic Attack', 54: 'Magic Protection', 178: 'Puppet Max Damage',
};

// Mastery (equipment level) bonus type: IMRBT=type, IMRBV=actual %.
const IMRB_TYPES: Record<string, string> = { 4: 'Max HP', 6: 'Bonus Damage' };

// Relic pockets are 32-35.
export function isRelicPocket(pocket?: number): boolean {
    return pocket !== undefined && pocket >= 32 && pocket <= 35;
}

// Pocket id -> inventory tab name. Sub-bag contents (100-299) are named by
// their bag item instead.
const POCKET_NAMES: Record<number, string> = {
    1: 'Cursor', 2: 'Inventory', 3: 'Appearance - Face', 4: 'Appearance - Hair',
    5: 'Equip - Clothing', 6: 'Equip - Gloves', 7: 'Equip - Shoes', 8: 'Equip - Head', 9: 'Equip - Robe',
    10: 'Equip - Main Hand', 11: 'Equip - Main Hand (set II)', 13: 'Equip - Off Hand', 14: 'Equip - Off Hand (set II)',
    16: 'Equip - Accessory L', 17: 'Equip - Accessory R',
    23: 'Quest', 24: 'Lost and Found', 31: 'Auction Pickup',
    32: 'Relic 1', 33: 'Relic 2', 34: 'Relic 3', 35: 'Relic 4',
    41: 'Combo Card Storage',
    43: 'Style - Clothing', 44: 'Style - Gloves', 45: 'Style - Shoes', 46: 'Style - Head', 47: 'Style - Robe', 48: 'Style - Face',
    49: 'Personal Items', 51: 'Halo', 53: 'VIP Inventory', 54: 'Stardust', 56: 'Fishing Bucket',
    61: 'Pon Inventory', 62: 'Echostone 1', 63: 'Echostone 2', 64: 'Echostone 3', 65: 'Currency Box',
    66: 'Dungeon Equipment', 72: 'Expanded Inventory', 81: 'Homestead Stone Storage', 83: 'Auction Items', 85: 'Recovery Inventory', 88: 'Moonlight Island Storage',
    90: 'Style - Tail', 91: 'Style - Accessory L', 92: 'Style - Accessory R',
    307: 'Homestead Storage', 3000: 'Quick Equipment Swap',
};

// Extra equipment sets a-t: 20 sets x 9 slots from pocket 2000.
const EXTRA_SET_SLOTS = ['Clothing', 'Gloves', 'Shoes', 'Head', 'Robe', 'Main Hand', 'Off Hand', 'Accessory L', 'Accessory R'];
const EXTRA_SET_FIRST = 2000, EXTRA_SET_COUNT = 20;

export function pocketName(pocket: number): string | undefined {
    const i = pocket - EXTRA_SET_FIRST;
    if (i >= 0 && i < EXTRA_SET_COUNT * EXTRA_SET_SLOTS.length) {
        const set = String.fromCharCode(97 + Math.floor(i / EXTRA_SET_SLOTS.length));
        return `Extra Set ${set} - ${EXTRA_SET_SLOTS[i % EXTRA_SET_SLOTS.length]}`;
    }
    return POCKET_NAMES[pocket];
}

const signed = (v: number) => `${v > 0 ? '+' : ''}${v}`;
const paramName = (code: number) => PARAM_NAMES[code] ?? `Param #${code}`;

// enchantText: "#prefix / #suffix" summary, '' when the item has none.
export function enchantText(h: Pick<Holder, 'enchantPrefix' | 'enchantSuffix'>): string {
    const parts: string[] = [];
    if (h.enchantPrefix) parts.push(`Prefix #${h.enchantPrefix}`);
    if (h.enchantSuffix) parts.push(`Suffix #${h.enchantSuffix}`);
    return parts.join(' / ');
}

// metalwareText: "#id Lv / ..." summary.
export function metalwareText(h: Pick<Holder, 'metalware'>): string {
    return (h.metalware ?? []).map(m => `#${m.id} Lv${m.level}`).join(' / ');
}

export interface TipEnchant { slot: string; id: number; effects: string[] }
export interface Tip {
    props: string[];
    imprint: string | null;
    enchants: TipEnchant[];
    bless: string[];
    relic: string[];
    upgrades: string[];
    special: string | null;
    energy: string | null;
    metalware: string[];
    colorGroups: { label: string; colors: string[] }[];
}

// buildTip assembles the tooltip sections; null when every section is empty.
export function buildTip(h: Holder): Tip | null {
    const meta = parseItemMetadata(h.metadata);

    const props: string[] = [];
    if (h.attackMax) props.push(`Attack ${h.attackMin ?? 0}~${h.attackMax}`);
    if (h.injuryMax) props.push(`Injury ${h.injuryMin ?? 0}~${h.injuryMax}%`);
    if (h.critical) props.push(`Critical ${h.critical}%`);
    if (h.balance) props.push(`Balance ${h.balance}%`);
    if (h.defense) props.push(`Defense ${h.defense}`);
    if (h.protection) props.push(`Protection ${h.protection}`);
    if (meta.MDEF) props.push(`Magic Defense ${meta.MDEF}`);
    if (meta.MPROT) props.push(`Magic Protection ${meta.MPROT}`);
    if (h.durabilityMax) {
        // On clothing patterns the durability field is the remaining uses.
        props.push(meta.FORMID
            ? `Uses left ${Math.floor((h.durability ?? 0) / 1000)}`
            : `Durability ${Math.floor((h.durability ?? 0) / 1000)}/${Math.floor(h.durabilityMax / 1000)}`);
    }
    if (meta.OWNER) props.push(`Exclusive to ${meta.OWNER}`);
    if (meta.SICID) props.push(`Reforged appearance: ${itemName(Number(meta.SICID))}`);

    const imprint = meta.IMRBV
        ? `${IMRB_TYPES[meta.IMRBT] ?? 'Equipment level bonus'} +${meta.IMRBV}%`
        : null;

    // Rolled values of each enchant's effect lines (kind 0 = prefix, 1 = suffix).
    const enchants: TipEnchant[] = [];
    const effectLine = (e: { code: number; value: number; condSkill?: number; condRank?: number }) =>
        `${paramName(e.code)} ${signed(e.value)}${e.condSkill ? ` (skill #${e.condSkill})` : ''}`;
    if (h.enchantPrefix) enchants.push({ slot: 'Prefix', id: h.enchantPrefix, effects: (h.prefixEffects ?? []).map(effectLine) });
    if (h.enchantSuffix) enchants.push({ slot: 'Suffix', id: h.enchantSuffix, effects: (h.suffixEffects ?? []).map(effectLine) });

    const bless = (h.blessEffects ?? []).map(effectLine);
    const relic = (h.relicEffects ?? []).map(e => `Effect #${e.code} ${signed(e.value)}`);

    // Upgrades (UPR1..n): "upgrade_id,effect_id,v1,v2,...".
    const upgrades: string[] = [];
    for (let i = 1; i <= 9; i++) {
        const raw = meta[`UPR${i}`];
        if (!raw) continue;
        const f = raw.split(',');
        const vals = f.slice(2).map(v => (Number(v) > 0 ? `+${v}` : v)).join(', ');
        upgrades.push(`Upgrade #${f[0]} (${vals})`);
    }
    // Special upgrade (EHTY 1024=R / 512=S, EHLV=step) and enhancement (IMEEL/IMEEML).
    const special = meta.EHLV
        ? `Special Upgrade ${meta.EHTY === '1024' ? 'R' : meta.EHTY === '512' ? 'S' : ''} (Step ${meta.EHLV})`
        : null;
    const energy = meta.IMEEL ? `Level ${meta.IMEEL}/${meta.IMEEML ?? '?'}` : null;

    const metalware = (h.metalware ?? []).map(m => `Ability #${m.id} (Lv ${m.level})`);

    // Two dye sets: the item's six colors as displayed (the appearance item's
    // when reforged), and OIC1..6 = the original colors.
    const colorGroups: { label: string; colors: string[] }[] = [];
    if (h.colors?.length) {
        colorGroups.push({ label: meta.SICID ? 'Colors (appearance)' : 'Colors', colors: h.colors });
    }
    const oic: string[] = [];
    for (let i = 1; i <= 6; i++) {
        const raw = meta[`OIC${i}`];
        if (raw === undefined) continue;
        oic.push((Number(raw) >>> 0 & 0xffffff).toString(16).padStart(6, '0'));
    }
    if (meta.SICID && oic.length) colorGroups.push({ label: 'Colors (original)', colors: oic });

    if (!props.length && !enchants.length && !metalware.length && !bless.length
        && !imprint && !upgrades.length && !special && !energy && !relic.length
        && !colorGroups.length) return null;
    return { props, imprint, enchants, bless, relic, upgrades, special, energy, metalware, colorGroups };
}
